import { FALLBACK_RATE_CARD } from "@/lib/whatsappCost/fallbackRateCard";
import type {
  WhatsappUsageResponse,
  WhatsappBillingStatusResponse,
  BroadcastEstimateApiResponse,
  AdminTenantsResponse,
} from "./whatsappBillingTypes";

/**
 * Returns mock RateCard data matching §2 specs.
 */
export function getMockRateCard() {
  return FALLBACK_RATE_CARD;
}

/**
 * Mock usage in the shape `GET /api/billing/whatsapp/usage` really returns. `month` is "YYYY-MM";
 * omitted means the current month, exactly as the endpoint reads it.
 */
export function getMockUsage(month?: string): WhatsappUsageResponse {
  const current = month ?? "2026-10";
  return {
    month: current,
    timezone: "Asia/Kolkata",
    currency: "INR",
    rates_are_assumed: true,
    rows: [
      { date: `${current}-02`, category: "marketing", billed_count: 520, free_count: 240, unpriced_count: 0, price: 448.81 },
      { date: `${current}-08`, category: "utility", billed_count: 110, free_count: 0, unpriced_count: 0, price: 12.65 },
      { date: `${current}-14`, category: "service", billed_count: 48, free_count: 910, unpriced_count: 0, price: 5.52 },
      { date: `${current}-19`, category: "authentication-international", billed_count: 0, free_count: 0, unpriced_count: 3, price: 0 },
    ],
    totals: {
      delivered: 1831, billed_count: 678, free_count: 1150, unpriced_count: 3,
      price: 466.98, gst: 84.06, price_incl_gst: 551.04,
    },
    free_breakdown: { billed: 678, free_entry_point: 240, free_tier: 910, unpriced: 3 },
    free_tier: [
      { phone_number_id: "phone_in_primary", display_number: "+91 98765 43210", used: 742, limit: 1000, remaining: 258 },
      { phone_number_id: "phone_in_secondary", display_number: "+91 98765 43211", used: 1000, limit: 1000, remaining: 0 },
    ],
    locked: false,
    handset_replies: 64,
    coexistence: true,
    // The call rates are not set yet, so a month's outgoing calls are all unpriced (v2).
    calls: {
      billed_count: 0, free_count: 0, unpriced_count: 0, pulses: 0, estimated_count: 0, price: 0,
      rates_are_assumed: true, rates_set: false,
    },
  };
}

/**
 * Returns mock billing configuration. Payment is handled by the Solution Partner, so there is
 * no payment-method state here.
 */
export function getMockBillingStatus(): WhatsappBillingStatusResponse {
  return {
    billing_mode: "partner_billed",
    timezone: "Asia/Kolkata",
    fep_window_hours: 72,
    currency: "INR",
    gst_rate: 0.18,
  };
}

/**
 * Returns mock broadcast estimate audience.
 */
export function getMockBroadcastEstimate(total = 1250): BroadcastEstimateApiResponse {
  // 1250 audience: 250 in FEP, 100 in US market, 900 standard India
  return {
    total,
    inFep: Math.min(total, 250),
    byMarket: [
      { market: "IN", count: Math.max(0, total - 100) },
      { market: "US", count: Math.min(total, 100) },
    ],
  };
}

/**
 * Returns mock tenants table for Platform Admin, in the shape `GET /api/admin/tenants` really
 * returns: counts and names only, and the cost fields null (not measured yet), except one row
 * that carries cost so the filled-in layout can be reviewed too.
 */
export function getMockAdminTenants(): AdminTenantsResponse {
  return {
    total: 4,
    rates_are_assumed: true,
    tenants: [
      {
        workspace_id: "ws_alpha_retail",
        workspace_name: "Alpha Retail Solutions",
        created_at: "2026-08-02T09:30:00Z",
        whatsapp_connected: true,
        whatsapp_numbers: 2,
        messages_30d: 5230,
        inbound_users_30d: 1180,
        outbound_users_30d: 1320,
        active_seats: 6,
        lost_window_leads: 18,
        billed_cost: 4075.42,
        billed_price: 4890.5,
        margin_percent: 20,
        margin_override: null,
        billing_channel: "partner_billed",
        delivered_30d: 5012,
        unpriced_count: 0,
        free_tier_used: 1240,
        free_tier_limit: 1000,
        custom_branding_enabled: true,
        needs_attention: true,
      },
      {
        workspace_id: "ws_bluewave_logistics",
        workspace_name: "Bluewave Logistics India",
        created_at: "2026-08-19T14:05:00Z",
        whatsapp_connected: true,
        whatsapp_numbers: 1,
        messages_30d: 1840,
        inbound_users_30d: 402,
        outbound_users_30d: 451,
        active_seats: 3,
        lost_window_leads: 12,
        billed_cost: 210.8,
        billed_price: 252.96,
        margin_percent: 20,
        margin_override: null,
        billing_channel: "partner_billed",
        delivered_30d: 1712,
        // Delivered but unpriceable: a template whose category was never read.
        unpriced_count: 34,
        free_tier_used: 980,
        free_tier_limit: 1000,
        custom_branding_enabled: false,
        needs_attention: true,
      },
      {
        workspace_id: "ws_coastal_spices",
        workspace_name: "Coastal Spices & Organics",
        created_at: "2026-09-04T11:20:00Z",
        whatsapp_connected: true,
        whatsapp_numbers: 1,
        messages_30d: 610,
        inbound_users_30d: 145,
        outbound_users_30d: 150,
        active_seats: 2,
        lost_window_leads: 0,
        billed_cost: 0,
        billed_price: 0,
        margin_percent: 25,
        margin_override: 25,
        billing_channel: "meta_direct",
        delivered_30d: 588,
        unpriced_count: 0,
        free_tier_used: 450,
        free_tier_limit: 1000,
        custom_branding_enabled: false,
        needs_attention: false,
      },
      {
        workspace_id: "ws_delta_pharma",
        workspace_name: null,
        created_at: "2026-09-15T08:45:00Z",
        whatsapp_connected: false,
        whatsapp_numbers: 0,
        messages_30d: 0,
        inbound_users_30d: 0,
        outbound_users_30d: 0,
        active_seats: 1,
        lost_window_leads: 0,
        billed_cost: 0,
        billed_price: 0,
        margin_percent: 20,
        margin_override: null,
        billing_channel: null,
        delivered_30d: 0,
        unpriced_count: 0,
        free_tier_used: 0,
        free_tier_limit: 1000,
        custom_branding_enabled: false,
        needs_attention: false,
      },
    ],
  };
}
